import axios from 'axios'
import jwtDecode from 'jwt-decode'
import { store } from 'lib/redux'
import { logout as authLogout } from 'ducks/auth/reducer'
import { logout as userLogout, setUser } from 'ducks/user/reducer'
import { clearAuthenticatedSession, getAccessToken, refreshAccessToken } from 'services/login/auth-session'
import { SESSION_TIMEOUT_MESSAGE_KEY } from 'constants/global.constants'

declare module 'axios' {
  export interface AxiosRequestConfig {
    __sessionRetried?: boolean
    preserveSessionOnForbidden?: boolean
    preserveSessionOnAuthFailure?: boolean
  }
}

axios.defaults.baseURL = process.env.REACT_APP_MAIN_API
axios.defaults.headers.common['Content-Type'] = 'application/json'
axios.defaults.withCredentials = true

type RoleType =
  | 'special_admin'
  | 'super_admin'
  | 'platform_admin'
  | 'super_platform_admin'
  | 'user'

interface IAccessTokenData {
  email?: string
  companyId?: number
  roleType?: RoleType
  userName?: string
  hasAdminPlatformManagement?: boolean
  hasQuestionnaireManagement?: boolean
}

const synchronizeSessionToken = (token: string): void => {
  const tokenData: IAccessTokenData = jwtDecode(token)
  store.dispatch(
    setUser({
      token,
      email: tokenData.email || '',
      companyId: tokenData.companyId || 0,
      userName: tokenData.userName || '',
      hasAdminPlatformManagement: Boolean(
        tokenData.hasAdminPlatformManagement,
      ),
      hasQuestionnaireManagement: Boolean(
        tokenData.hasQuestionnaireManagement,
      ),
    }),
  )
}

axios.interceptors.request.use(
  (request) => {
    const token = getAccessToken()

    if (token && request.headers) {
      // eslint-disable-next-line
      request.headers['Authorization'] = `Bearer ${token}`
    }

    return request
  },
  (error) => {
    window.location.href = '/login'
    return Promise.reject(error)
  }
)

// These flows precede or manage a platform session; their errors belong to
// their own form instead of causing a global logout.
const isAuthenticationRoute = (url: string): boolean => {
  let path = url
  try {
    path = new URL(url, window.location.origin).pathname
  } catch {
    // Relative and malformed URLs fall back to the original value below.
  }
  return (
    /(?:^|\/)auth\/(?:login|refresh|csrf|logout|action-codes|activation-links|password-recovery-links)(?:\/|$)/.test(path) ||
    /(?:^|\/)(?:auth\/password-recovery|user\/new-password|person\/user\/activate)(?:\/|$)/.test(path) ||
    path.includes('/register') ||
    path.includes('/signup')
  )
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status
    const request = error.config
    const requestSessionControl = request
    const url = request?.url || ''
    const preserveSessionOnAuthFailure =
      requestSessionControl?.preserveSessionOnAuthFailure === true ||
      (status === 403 && requestSessionControl?.preserveSessionOnForbidden === true)
    if (
      !preserveSessionOnAuthFailure &&
      (status === 401 || status === 403) &&
      request &&
      !requestSessionControl?.__sessionRetried &&
      !isAuthenticationRoute(url) &&
      getAccessToken()
    ) {
      requestSessionControl.__sessionRetried = true
      const session = await refreshAccessToken()

      if (session) {
        synchronizeSessionToken(session.token)
        request.headers = request.headers || {}
        request.headers.Authorization = `Bearer ${session.token}`
        return axios(request)
      }
    }

    if (
      !preserveSessionOnAuthFailure &&
      (status === 401 || status === 403) &&
      !isAuthenticationRoute(url)
    ) {
      clearAuthenticatedSession()
      store.dispatch(authLogout())
      store.dispatch(userLogout())
      sessionStorage.setItem(
        SESSION_TIMEOUT_MESSAGE_KEY,
        'Tu sesión expiró o ya no es válida. Por favor inicia sesión nuevamente para continuar.'
      )
      window.location.href = '/login'
    }

    return Promise.reject(error)
  }
)

export default axios
